/**
 * Markdown编辑器组件
 * 基于@uiw/react-md-editor实现，提供完整的Markdown编辑功能
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import MDEditor from '@uiw/react-md-editor';
import { Button, Tooltip, Space, Divider } from 'antd';
import { 
  BoldOutlined, 
  ItalicOutlined, 
  UnderlineOutlined,
  StrikethroughOutlined,
  LinkOutlined, 
  UnorderedListOutlined,
  OrderedListOutlined,
  CodeOutlined,
  TableOutlined,
  SaveOutlined,
  PictureOutlined
} from '@ant-design/icons';
import { useMarkdownEditorStyles } from '../../styles/components/markdown/MarkdownEditor';
import ImageSelectorModal from './ImageSelectorModal';
import { isUrlInWhitelist } from '../../utils/imageWhitelist';

// 导入样式文件
import "@uiw/react-md-editor/markdown-editor.css";
import "@uiw/react-markdown-preview/markdown.css";
import "katex/dist/katex.min.css";

// 导入Mermaid和KaTeX
import mermaid from 'mermaid';
import katex from 'katex';
import { getCodeString } from 'rehype-rewrite';

// 全局初始化Mermaid配置
mermaid.initialize({
  startOnLoad: false,
  theme: 'default',
  securityLevel: 'loose',
  flowchart: {
    useMaxWidth: true,
    htmlLabels: true
  },
  // 添加更多配置以提高稳定性
  sequence: {
    useMaxWidth: true,
    diagramMarginX: 50,
    diagramMarginY: 10
  },
  gantt: {
    useMaxWidth: true
  }
});

// 自定义工具栏组件
const ToolbarPlugin = ({ onSave, editorRef, onChange, value, attachments = [], displayFiles = [] }) => {
  const { styles } = useMarkdownEditorStyles();
  const [isImageSelectorOpen, setIsImageSelectorOpen] = useState(false);
  
  // 获取当前光标位置和选中文本
  const getSelectionInfo = useCallback(() => {
    if (editorRef.current) {
      // 尝试获取textarea元素
      const editorElement = editorRef.current;
      let textarea = null;
      
      // 方法1: 直接访问textarea属性
      if (editorElement.textarea) {
        textarea = editorElement.textarea;
      }
      // 方法2: 通过DOM查询
      else if (editorElement.querySelector) {
        textarea = editorElement.querySelector('textarea');
      }
      // 方法3: 通过ref的DOM元素
      else if (editorElement.$el) {
        textarea = editorElement.$el.querySelector('textarea');
      }
      // 方法4: 通过ref的DOM属性
      else if (editorElement.dom) {
        textarea = editorElement.dom.querySelector('textarea');
      }
      
      if (textarea) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const text = textarea.value;
        const selectedText = text.substring(start, end);
        
        return {
          start,
          end,
          text,
          selectedText,
          textarea
        };
      }
    }
    return null;
  }, [editorRef]);

  // 在指定位置插入文本
  const insertTextAtPosition = useCallback((text, start, end, selectedText) => {
    if (onChange) {
      const currentValue = value || '';
      const before = currentValue.substring(0, start);
      const after = currentValue.substring(end);
      const newValue = before + text + after;
      onChange(newValue);
      
      // 设置新的光标位置
      setTimeout(() => {
        const selectionInfo = getSelectionInfo();
        if (selectionInfo && selectionInfo.textarea) {
          const newStart = start + text.length;
          selectionInfo.textarea.setSelectionRange(newStart, newStart);
          selectionInfo.textarea.focus();
        }
      }, 0);
    }
  }, [onChange, value, getSelectionInfo]);

  // 处理图片选择
  const handleImageSelect = useCallback((imageMarkdown, selectedImage) => {
    const selectionInfo = getSelectionInfo();
    if (selectionInfo) {
      const { start, end } = selectionInfo;
      // 在光标位置插入图片Markdown语法
      insertTextAtPosition(imageMarkdown, start, end, '');
    } else {
      // 如果无法获取选择信息，在末尾插入
      if (onChange) {
        const currentValue = value || '';
        const newValue = currentValue + '\n' + imageMarkdown;
        onChange(newValue);
      }
    }
  }, [getSelectionInfo, insertTextAtPosition, onChange, value]);

  // 插入格式化文本
  const insertFormattedText = useCallback((prefix, suffix, placeholder = '文本') => {
    
    const selectionInfo = getSelectionInfo();
    if (selectionInfo) {
      const { start, end, selectedText } = selectionInfo;
      
      if (selectedText) {
        // 如果有选中的文本，用格式包围它
        const formattedText = prefix + selectedText + suffix;
        insertTextAtPosition(formattedText, start, end, selectedText);
      } else {
        // 如果没有选中文本，插入占位符
        const formattedText = prefix + placeholder + suffix;
        insertTextAtPosition(formattedText, start, end, '');
      }
    } else {
      // 如果无法获取选择信息，在末尾插入
      if (onChange) {
        const currentValue = value || '';
        const newValue = currentValue + prefix + placeholder + suffix;
        onChange(newValue);
      }
    }
  }, [getSelectionInfo, insertTextAtPosition, onChange, value]);

  // 插入标题
  const insertHeading = useCallback((level) => {
    const selectionInfo = getSelectionInfo();
    if (selectionInfo) {
      const { start, end, selectedText } = selectionInfo;
      
      if (selectedText) {
        // 如果有选中的文本，将其转换为标题
        // 移除选中文本中可能存在的标题符号
        const cleanText = selectedText.replace(/^#+\s*/, '').trim();
        const headingText = '#'.repeat(level) + ' ' + cleanText;
        insertTextAtPosition(headingText, start, end, selectedText);
      } else {
        // 如果没有选中文本，插入标题符号和占位符
        const headingText = '#'.repeat(level) + ' 标题' + level;
        insertTextAtPosition(headingText, start, end, '');
      }
    } else {
      // 如果无法获取选择信息，在末尾插入
      if (onChange) {
        const currentValue = value || '';
        const headingText = '#'.repeat(level) + ' 标题' + level;
        const newValue = currentValue + headingText;
        onChange(newValue);
      }
    }
  }, [getSelectionInfo, insertTextAtPosition, onChange, value]);

  // 插入代码块
  const insertCodeBlock = useCallback(() => {
    const selectionInfo = getSelectionInfo();
    if (selectionInfo) {
      const { start, end, selectedText } = selectionInfo;
      
      if (selectedText) {
        // 如果有选中的文本，将其包装为代码块
        const codeBlock = `\n\`\`\`\n${selectedText}\n\`\`\`\n`;
        insertTextAtPosition(codeBlock, start, end, selectedText);
      } else {
        // 如果没有选中文本，插入代码块模板
        const codeBlock = '\n```\n代码块\n```\n';
        insertTextAtPosition(codeBlock, start, end, '');
      }
    } else {
      // 如果无法获取选择信息，在末尾插入
      if (onChange) {
        const currentValue = value || '';
        const codeBlock = '\n```\n代码块\n```\n';
        const newValue = currentValue + codeBlock;
        onChange(newValue);
      }
    }
  }, [getSelectionInfo, insertTextAtPosition, onChange, value]);

  // 插入表格
  const insertTable = useCallback(() => {
    const tableMarkdown = `
| 列1 | 列2 | 列3 |
|-----|-----|-----|
| 内容1 | 内容2 | 内容3 |
| 内容4 | 内容5 | 内容6 |
`;
    const selectionInfo = getSelectionInfo();
    if (selectionInfo) {
      const { start, end } = selectionInfo;
      insertTextAtPosition(tableMarkdown, start, end, '');
    } else {
      if (onChange) {
        const currentValue = value || '';
        const newValue = currentValue + tableMarkdown;
        onChange(newValue);
      }
    }
  }, [getSelectionInfo, insertTextAtPosition, onChange, value]);

  // 插入链接
  const insertLink = useCallback(() => {
    const linkText = '[链接文本](url)';
    const selectionInfo = getSelectionInfo();
    if (selectionInfo) {
      const { start, end } = selectionInfo;
      insertTextAtPosition(linkText, start, end, '');
    } else {
      if (onChange) {
        const currentValue = value || '';
        const newValue = currentValue + linkText;
        onChange(newValue);
      }
    }
  }, [getSelectionInfo, insertTextAtPosition, onChange, value]);

  // 插入列表
  const insertList = useCallback((isOrdered = false) => {
    const selectionInfo = getSelectionInfo();
    if (selectionInfo) {
      const { start, end, selectedText } = selectionInfo;
      
      if (selectedText) {
        // 如果有选中的文本，将其转换为列表项
        const lines = selectedText.split('\n').filter(line => line.trim());
        const listItems = lines.map((line, index) => {
          // 清理现有的列表标记
          const cleanLine = line.replace(/^[\s]*[-*+]\s*/, '').replace(/^[\s]*\d+\.\s*/, '').trim();
          if (isOrdered) {
            return `${index + 1}. ${cleanLine}`;
          } else {
            return `* ${cleanLine}`;
          }
        });
        const listText = listItems.join('\n');
        insertTextAtPosition(listText, start, end, selectedText);
      } else {
        // 如果没有选中文本，插入列表模板
        const listText = isOrdered ? 
          '\n1. 列表项\n2. 列表项\n3. 列表项\n' : 
          '\n* 列表项\n* 列表项\n* 列表项\n';
        insertTextAtPosition(listText, start, end, '');
      }
    } else {
      // 如果无法获取选择信息，在末尾插入
      if (onChange) {
        const currentValue = value || '';
        const listText = isOrdered ? 
          '\n1. 列表项\n2. 列表项\n3. 列表项\n' : 
          '\n* 列表项\n* 列表项\n* 列表项\n';
        const newValue = currentValue + listText;
        onChange(newValue);
      }
    }
  }, [getSelectionInfo, insertTextAtPosition, onChange, value]);

  return (
    <div className={styles.toolbar}>
      <Space>
        {/* 文本格式化 */}
        <Tooltip title="粗体 (Ctrl+B)">
          <Button
            type="text"
            icon={<BoldOutlined />}
            onClick={() => {
              insertFormattedText('**', '**', '粗体文本');
            }}
            size="small"
          />
        </Tooltip>
        <Tooltip title="斜体 (Ctrl+I)">
          <Button
            type="text"
            icon={<ItalicOutlined />}
            onClick={() => {
              insertFormattedText('*', '*', '斜体文本');
            }}
            size="small"
          />
        </Tooltip>
        <Tooltip title="下划线 (Ctrl+U)">
          <Button
            type="text"
            icon={<UnderlineOutlined />}
            onClick={() => {
              insertFormattedText('<u>', '</u>', '下划线文本');
            }}
            size="small"
          />
        </Tooltip>
        <Tooltip title="删除线">
          <Button
            type="text"
            icon={<StrikethroughOutlined />}
            onClick={() => {
              insertFormattedText('~~', '~~', '删除线文本');
            }}
            size="small"
          />
        </Tooltip>
        
        <Divider type="vertical" />
        
        {/* 链接 */}
        <Tooltip title="插入链接">
          <Button
            type="text"
            icon={<LinkOutlined />}
            onClick={insertLink}
            size="small"
          />
        </Tooltip>
        
        <Divider type="vertical" />
        
        {/* 标题 */}
        <Tooltip title="一级标题">
          <Button
            type="text"
            onClick={() => insertHeading(1)}
            size="small"
          >
            H1
          </Button>
        </Tooltip>
        <Tooltip title="二级标题">
          <Button
            type="text"
            onClick={() => insertHeading(2)}
            size="small"
          >
            H2
          </Button>
        </Tooltip>
        <Tooltip title="三级标题">
          <Button
            type="text"
            onClick={() => insertHeading(3)}
            size="small"
          >
            H3
          </Button>
        </Tooltip>
        
        <Divider type="vertical" />
        
        {/* 列表 */}
        <Tooltip title="无序列表">
          <Button
            type="text"
            icon={<UnorderedListOutlined />}
            onClick={() => insertList(false)}
            size="small"
          />
        </Tooltip>
        <Tooltip title="有序列表">
          <Button
            type="text"
            icon={<OrderedListOutlined />}
            onClick={() => insertList(true)}
            size="small"
          />
        </Tooltip>
        
        <Divider type="vertical" />
        
        {/* 代码 */}
        <Tooltip title="代码块">
          <Button
            type="text"
            icon={<CodeOutlined />}
            onClick={insertCodeBlock}
            size="small"
          />
        </Tooltip>
        
        <Divider type="vertical" />
        
        {/* 表格 */}
        <Tooltip title="插入表格">
          <Button
            type="text"
            icon={<TableOutlined />}
            onClick={insertTable}
            size="small"
          />
        </Tooltip>
        
        <Divider type="vertical" />
        
        {/* 图片 */}
        <Tooltip title="插入图片">
          <Button
            type="text"
            icon={<PictureOutlined />}
            onClick={() => {
              setIsImageSelectorOpen(true);
            }}
            size="small"
          />
        </Tooltip>
        
        <Divider type="vertical" />
        
        {/* 保存 */}
        <Tooltip title="保存 (Ctrl+S)">
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={onSave}
            size="small"
          >
            保存
          </Button>
        </Tooltip>
      </Space>

      {/* 图片选择器Modal */}
      <ImageSelectorModal
        visible={isImageSelectorOpen}
        onClose={() => setIsImageSelectorOpen(false)}
        onSelect={handleImageSelect}
        attachments={attachments}
        displayFiles={displayFiles}
      />
    </div>
  );
};

// 主编辑器组件
const MarkdownEditor = ({ 
  value, 
  onChange, 
  onSave, 
  placeholder = "开始编写您的文档...",
  height = 500,
  attachments = [],
  displayFiles = []
}) => {
  const { styles } = useMarkdownEditorStyles();
  const editorRef = useRef();

  // 键盘快捷键处理
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey || e.metaKey) {
        switch (e.key) {
          case 's':
            e.preventDefault();
            onSave?.();
            break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onSave]);

  return (
    <div className={styles.editorContainer}>
      <ToolbarPlugin 
        onSave={onSave} 
        editorRef={editorRef} 
        onChange={onChange}
        value={value}
        attachments={attachments}
        displayFiles={displayFiles}
      />
      <div className={styles.editorContent}>
        <MDEditor
          ref={editorRef}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          height={height}
          preview="live"
          hideToolbar={true}
          className="border-none bg-transparent"
          previewOptions={{
            components: {
              ul: ({ children, ...props }) => (
                <ul className="list-disc pl-8 my-4" {...props}>
                  {children}
                </ul>
              ),
              ol: ({ children, ...props }) => (
                <ol className="list-decimal pl-8 my-4" {...props}>
                  {children}
                </ol>
              ),
              li: ({ children, ...props }) => (
                <li className="my-1 list-item" {...props}>
                  {children}
                </li>
              ),
              // 添加图片白名单验证
              img: ({ src, alt, ...props }) => {
                // 验证图片URL是否在白名单中
                if (!isUrlInWhitelist(src)) {
                  return (
                    <div className="bg-red-50 border border-red-200 rounded p-2 mb-2 flex items-center text-red-600 text-sm">
                      <svg className="mr-2 w-4 h-4 fill-red-500" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                      </svg>
                      不允许上传外部图片
                    </div>
                  );
                }
                
                return (
                  <img
                    src={src}
                    alt={alt}
                    className="max-w-full h-auto rounded mb-2"
                    {...props}
                  />
                );
              },
              // 添加Mermaid图表和数学公式支持
              code: ({ children, className, ...props }) => {
                const isMermaid = className && /^language-mermaid/.test(className.toLowerCase());
                const isMath = className && /^language-math/.test(className.toLowerCase());
                
                if (isMermaid) {
                  return <MermaidRenderer children={children} className={className} {...props} />;
                }
                
                if (isMath) {
                  return <MathRenderer code={children ? children[0] || "" : ""} />;
                }
                
                return <code className={className} {...props}>{children}</code>;
              }
            }
          }}
        />
      </div>
    </div>
  );
};

// 生成随机ID
const randomid = () => parseInt(String(Math.random() * 1e15), 10).toString(36);

// 防抖函数
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// 安全的哈希函数，支持Unicode字符
const generateHash = (str) => {
  if (!str) return '';
  
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // 转换为32位整数
  }
  
  // 转换为正数并转为16进制字符串
  return Math.abs(hash).toString(16).slice(0, 8);
};

// Mermaid图表渲染组件
const MermaidRenderer = ({ children = [], className, ...props }) => {
  const demoid = useRef(`dome${randomid()}`);
  const [container, setContainer] = useState(null);
  const [svgContent, setSvgContent] = useState('');
  const [error, setError] = useState('');
  const isMermaid = className && /^language-mermaid/.test(className.toLowerCase());
  
  // 获取代码内容，优先使用getCodeString，如果没有则使用children
  const code = props.node?.children ? getCodeString(props.node.children) : (children ? children[0] || "" : "");
  
  // 使用防抖减少频繁渲染
  const debouncedCode = useDebounce(code, 300);
  

  useEffect(() => {
    if (container && isMermaid && demoid.current && debouncedCode) {
      // 检查是否已经渲染过相同的内容
      const codeHash = generateHash(debouncedCode); // 使用安全的哈希函数
      const existingHash = container.getAttribute('data-code-hash');
      
      if (existingHash === codeHash && svgContent) {
        // 如果代码没有变化且已经渲染过，直接返回
        return;
      }
      
      try {
        // 渲染Mermaid图表（使用全局配置）
        mermaid.render(demoid.current, debouncedCode).then(({ svg, bindFunctions }) => {
          // 检查容器是否仍然存在
          if (container && container.parentNode) {
            try {
              container.innerHTML = svg;
              container.setAttribute('data-code-hash', codeHash);
              setSvgContent(svg);
              setError('');
              
              if (bindFunctions) {
                bindFunctions(container);
              }
            } catch (domError) {
              console.error('DOM操作错误:', domError);
            }
          }
        }).catch((error) => {
          console.error('Mermaid渲染错误:', error);
          setError(error.message);
          if (container && container.parentNode) {
            try {
              container.innerHTML = `<div style="color: red;">Mermaid渲染失败: ${error.message}</div>`;
            } catch (domError) {
              console.error('DOM操作错误:', domError);
            }
          }
        });
      } catch (err) {
        console.error('Mermaid初始化错误:', err);
        setError(err.message);
        if (container && container.parentNode) {
          try {
            container.innerHTML = `<div style="color: red;">Mermaid初始化失败: ${err.message}</div>`;
          } catch (domError) {
            console.error('DOM操作错误:', domError);
          }
        }
      }
    }
  }, [container, isMermaid, debouncedCode, demoid, svgContent]);

  const refElement = useCallback((node) => {
    if (node !== null && node !== container) {
      setContainer(node);
    } else if (node === null && container !== null) {
      setContainer(null);
    }
  }, [container]);

  // 清理函数
  useEffect(() => {
    return () => {
      if (container && container.parentNode) {
        try {
          // 清理容器内容
          container.innerHTML = '';
        } catch (error) {
          console.warn('清理容器时出错:', error);
        }
      }
    };
  }, [container]);

  // 组件卸载时清理
  useEffect(() => {
    return () => {
      // 清理渲染状态
      setSvgContent('');
      setError('');
      setContainer(null);
    };
  }, []);

  if (isMermaid) {
    return (
      <div className="my-4 text-center">
        <code id={demoid.current} className="hidden" />
        <code 
          ref={refElement} 
          data-name="mermaid"
          className="block min-h-25 border border-gray-200 rounded p-4 bg-gray-50"
        />
      </div>
    );
  }
  
  return <code className={className} {...props}>{children}</code>;
};

// KaTeX数学公式渲染组件
const MathRenderer = ({ code }) => {
  const [rendered, setRendered] = useState('');
  const [error, setError] = useState('');
  const [isRendering, setIsRendering] = useState(false);

  useEffect(() => {
    if (code && !isRendering) {
      setIsRendering(true);
      
      try {
        // 渲染数学公式
        const html = katex.renderToString(code, {
          throwOnError: false,
          errorColor: '#cc0000',
          displayMode: true,
          output: 'html'
        });
        setRendered(html);
        setError('');
      } catch (err) {
        setError(`数学公式渲染失败: ${err.message}`);
        console.error('KaTeX渲染错误:', err);
      } finally {
        setIsRendering(false);
      }
    }
  }, [code, isRendering]);

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded p-3 my-2 text-red-600">
        <strong>数学公式渲染失败:</strong> {error}
      </div>
    );
  }

  if (isRendering) {
    return (
      <div className="bg-gray-100 border border-gray-300 rounded p-3 my-2 text-center text-gray-600">
        正在渲染数学公式...
      </div>
    );
  }

  return (
    <div 
      className="my-4 text-center overflow-auto p-4 bg-gray-50 border border-gray-200 rounded"
      dangerouslySetInnerHTML={{ __html: rendered }}
    />
  );
};

export default MarkdownEditor;
